import axios from 'axios';
import BasePage from '../../components/base-page/base-page';
import defineCustomerReviewCardElement from '../../components/customer-review-card/customer-review-card';
import GoUpButton from '../../components/go-up-button/go-up-button';
import type { UiElement } from '../../../shared/ui-element/ui-element';

import './customer-reviews.scss';


type Review = {
  author: string;
  date: string;
  comment: string;
  isPositive: boolean;
  response?: {
    type: 'setapp' | 'vendor';
    comment: string;
    date: string;
  };
}

class CustomerReviewsPage extends BasePage {
  reviews?: Review[];
  reviewsPage = 0;
  areReviewsFetching = false;

  ui = {
    root: '.js-root-element',
    loadMoreButton: '.js-load-more-button',
  };

  uiCollections = {
    reviewsColumns: '.js-reviews-column',
  };

  events = {
    'click @ui.loadMoreButton': 'loadMoreReviews',
  };

  onInit() {
    super.onInit();

    defineCustomerReviewCardElement();
    (new GoUpButton(this.ui.root) as UiElement).init();
  }

  async loadMoreReviews() {
    if (this.areReviewsFetching) return;

    this.areReviewsFetching = true;

    try {
      const response = await axios.get<Review[]>(`customer-reviews/${this.reviewsPage + 1}`);
      this.reviews = response.data;

      this.reviewsPage += 1;

      const { amountOfReviewsPerPage } = (this.ui.root as unknown as HTMLElement).dataset;
      if (Number(amountOfReviewsPerPage) >= this.reviews.length) {
        (this.ui.loadMoreButton as unknown as HTMLElement).classList.add('d-none');
      } else {
        // Remove last review, which is loaded to understand if the next page exists
        this.reviews.pop();
      }

      for (let reviewIdx = 0; reviewIdx < this.reviews.length; reviewIdx++) {
        const reviewCardContainer = document.createElement('div');
        reviewCardContainer.classList.add('customer-reviews__card-container');
        reviewCardContainer.appendChild(this.createCustomerReviewCard(this.reviews[reviewIdx]!));

        const reviewColumns = (this.uiCollections.reviewsColumns as unknown as HTMLElement[]);
        const columnsHeights = reviewColumns.map((column) => column.clientHeight);
        const columnIdx = columnsHeights.indexOf(Math.min(...columnsHeights));

        // appendChild is a sync method, but somehow await helps to append
        // review cards asynchronously - in that way we can get proper columns heights
        await reviewColumns[columnIdx]!.appendChild(reviewCardContainer);
      }
    } finally {
      this.areReviewsFetching = false;
    }
  }

  createCustomerReviewCard(review: Review) {
    const reviewCard = document.createElement('customer-review-card');
    reviewCard.setAttribute('author', review.author);
    reviewCard.setAttribute('date', review.date);
    reviewCard.setAttribute('comment', review.comment);
    if (review.isPositive) {
      reviewCard.setAttribute('isPositive', '');
    }

    if (review.response) {
      const {
        responseFromSetappCaption,
        responseFromDeveloperCaption,
      } = (this.ui.root as unknown as HTMLElement).dataset;

      const responseTitle = (review.response.type === 'setapp') ? responseFromSetappCaption : responseFromDeveloperCaption;

      reviewCard.setAttribute('responseTitle', responseTitle!);
      reviewCard.setAttribute('responseComment', review.response.comment);
      reviewCard.setAttribute('responseDate', review.response.date);
    }

    return reviewCard;
  }
}

export default CustomerReviewsPage;

(CustomerReviewsPage as UiElement).initOnDomLoaded();
